import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { ChapterContentProps, ChapterContentRef } from './types';
import { decryptContent } from './utils/contentDecryption';
import ThemeProvider from './ThemeProvider';
import SecurityLayer from './SecurityLayer';
import InitialScrollPosition from './InitialScrollPosition';
import { getContentPaddingBottom } from '@/constants/layout-constants';
import { useIsMobile } from '@/hooks/use-mobile';
import { HIGHLIGHT_COLORS, HighlightColor } from '@/hooks/useHighlights';

const ChapterContent = forwardRef<ChapterContentRef, ChapterContentProps>(({
  content,
  fontSize = '16px',
  fontFamily = 'sans-serif',
  alignment = 'justify',
  theme = 'light',
  initialScrollPosition = 0,
  isInitialPositionReady = false,
  onScroll,
  onTap,
  footerVisible = true,
  audioPlayerVisible = false,
  highlights = [],
  onHighlightCreate,
  onHighlightDelete,
  processHTMLWithHighlights,
}, ref) => {

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [decryptedContent, setDecryptedContent] = useState('');
  const [contentLoaded, setContentLoaded] = useState(false);
  const isMobile = useIsMobile();

  const lastScrollTop = useRef(0);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const scrollDirectionTimeout = useRef<NodeJS.Timeout | null>(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const selectionStartTime = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const touchStartPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  console.log('[CHAPTER_CONTENT] Component rendered - THIS IS THE SINGLE SCROLL CONTROLLER');

  useImperativeHandle(ref, () => ({
    scrollToPosition: (position: number) => {
      if (containerRef.current) {
        console.log(`[CHAPTER_CONTENT] Scrolling to position: ${position}px`);
        containerRef.current.scrollTop = position;
      }
      return position;
    },
    getCurrentPosition: () => {
      const position = containerRef.current?.scrollTop || 0;
      console.log(`[CHAPTER_CONTENT] Getting current position: ${position}px`);
      return position;
    },
    getPercentRead: () => {
      if (!containerRef.current || !contentRef.current) return 0;

      const scrollPosition = containerRef.current.scrollTop;
      const totalHeight = contentRef.current.scrollHeight - containerRef.current.clientHeight;
      const percent = totalHeight > 0 ? Math.min(scrollPosition / totalHeight, 1) : 0;

      console.log(`[CHAPTER_CONTENT] Calculating percent: ${percent.toFixed(4)} (scroll: ${scrollPosition}, total: ${totalHeight})`);
      return percent;
    },
    getContentHeight: () => {
      return contentRef.current?.scrollHeight || 0;
    },
    getContainerHeight: () => {
      return containerRef.current?.clientHeight || 0;
    },
    getContainerRef: () => {
      return contentRef.current;
    }
  }));

  useEffect(() => {
    const loadContent = async () => {
      try {
        const decrypted = await decryptContent(content || '');
        setDecryptedContent(decrypted);

        const timer = setTimeout(() => {
          setContentLoaded(true);
          console.log('[CHAPTER_CONTENT] Content loaded and ready for initial scroll');
        }, 100);

        return () => clearTimeout(timer);
      } catch (error) {
        console.error('[CHAPTER_CONTENT] Error decrypting content:', error);
        setDecryptedContent('<p>Error: Unable to decrypt content</p>');
        setContentLoaded(true);
      }
    };

    loadContent();
  }, [content]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.fontSize = fontSize;
      contentRef.current.style.fontFamily = fontFamily;
      contentRef.current.style.textAlign = alignment;
    }
  }, [fontSize, fontFamily, alignment]);

  useEffect(() => {
    if (isInitialPositionReady && initialScrollPosition > 0) {
      console.log(`[CHAPTER_CONTENT] Initial scroll position ready: ${initialScrollPosition}px, content loaded: ${contentLoaded}`);
    }
  }, [isInitialPositionReady, initialScrollPosition, contentLoaded]);

  // ========== LÓGICA DE SELEÇÃO UNIVERSAL (WEB + MOBILE) ==========
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ========== EVENTOS MOUSE (WEB) ==========
    const handleMouseDown = (e: MouseEvent) => {
      console.log('[HIGHLIGHT_DEBUG] Mouse down event');
      setIsSelecting(true);
      selectionStartTime.current = Date.now();
      window.getSelection()?.removeAllRanges();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isSelecting) {
        console.log('[HIGHLIGHT_DEBUG] Mouse move during selection');
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      console.log('[HIGHLIGHT_DEBUG] Mouse up event triggered');
      handleSelectionEnd();
    };

    // ========== EVENTOS TOUCH (MOBILE) ==========
    const handleTouchStart = (e: TouchEvent) => {
      console.log('[HIGHLIGHT_DEBUG] Touch start event');
      const touch = e.touches[0];
      touchStartTime.current = Date.now();
      touchStartPosition.current = { x: touch.clientX, y: touch.clientY };
      setIsSelecting(true);
      window.getSelection()?.removeAllRanges();
    };

    const handleTouchMove = (e: TouchEvent) => {
      console.log('[HIGHLIGHT_DEBUG] Touch move during selection');
    };

    const handleTouchEnd = (e: TouchEvent) => {
      console.log('[HIGHLIGHT_DEBUG] Touch end event triggered');

      const touchDuration = Date.now() - touchStartTime.current;
      const touch = e.changedTouches[0];
      const touchDistance = Math.sqrt(
        Math.pow(touch.clientX - touchStartPosition.current.x, 2) +
        Math.pow(touch.clientY - touchStartPosition.current.y, 2)
      );

      if (touchDuration < 500 && touchDistance < 10) {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          console.log('[HIGHLIGHT_DEBUG] Quick tap detected on mobile');
          setIsSelecting(false);
          onTap?.();
          return;
        }
      }

      setTimeout(() => {
        handleSelectionEnd();
      }, 100);
    };

    // ========== FUNÇÃO COMUM PARA PROCESSAR SELEÇÃO ==========
    const handleSelectionEnd = () => {
      const selection = window.getSelection();

      if (!selection || selection.isCollapsed) {
        setIsSelecting(false);
        const selectionDuration = Date.now() - selectionStartTime.current;
        if (selectionDuration < 300) {
          console.log('[HIGHLIGHT_DEBUG] Quick interaction detected, calling onTap');
          onTap?.();
        }
        return;
      }

      if (!container.contains(selection.anchorNode)) {
        console.log('[HIGHLIGHT_DEBUG] Selection outside container');
        setIsSelecting(false);
        onTap?.();
        return;
      }

      const range = selection.getRangeAt(0);
      const selectedText = selection.toString().trim();

      console.log('[HIGHLIGHT_DEBUG] Text selected:', {
        text: selectedText.substring(0, 50) + '...',
        length: selectedText.length,
        hasHighlightCreate: !!onHighlightCreate,
        isMobile
      });

      if (selectedText.length > 0 && onHighlightCreate && decryptedContent) {
        try {
          // === NOVA LÓGICA DE CÁLCULO DE OFFSETS QUE RESPEITA O DOM ===
          const preSelectionRange = document.createRange();
          preSelectionRange.setStart(container, 0);
          preSelectionRange.setEnd(range.startContainer, range.startOffset);

          const tempDivStart = document.createElement('div');
          tempDivStart.appendChild(preSelectionRange.cloneContents());
          const startOffset = tempDivStart.textContent.length;
          const endOffset = startOffset + selectedText.length;

          console.log('[HIGHLIGHT_DEBUG] Calculated offsets (NEW):', {
            startOffset,
            endOffset,
            selectedText: selectedText.substring(0, 30) + '...',
            platform: isMobile ? 'mobile' : 'desktop'
          });

          onHighlightCreate(selectedText, startOffset, endOffset);

          setTimeout(() => {
            window.getSelection()?.removeAllRanges();
          }, isMobile ? 200 : 100);

        } catch (error) {
          console.error('[HIGHLIGHT_DEBUG] Error calculating offsets:', error);
        }
      }

      setIsSelecting(false);
    };

    // ========== EVENTO PARA DELETAR HIGHLIGHTS ==========
    const handleHighlightClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      console.log('[HIGHLIGHT_DEBUG] Click/Touch event on:', target.className);

      if (target.classList.contains('highlight-mark') && onHighlightDelete) {
        const highlightId = target.getAttribute('data-highlight-id');
        console.log('[HIGHLIGHT_DEBUG] Clicked highlight with ID:', highlightId);

        if (highlightId) {
          e.preventDefault();
          e.stopPropagation();

          if (confirm('Remover este destaque?')) {
            onHighlightDelete(highlightId);
          }
        }
      }
    };

    // ========== REGISTRAR TODOS OS EVENT LISTENERS ==========
    container.addEventListener('mousedown', handleMouseDown, { passive: false });
    container.addEventListener('mousemove', handleMouseMove, { passive: true });
    container.addEventListener('mouseup', handleMouseUp, { passive: false });
    container.addEventListener('click', handleHighlightClick, { passive: false });
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    container.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('highlight-mark')) {
        e.preventDefault();
        if (isMobile && onHighlightDelete) {
          const highlightId = target.getAttribute('data-highlight-id');
          if (highlightId && confirm('Remover este destaque?')) {
            onHighlightDelete(highlightId);
          }
        }
      }
    });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('click', handleHighlightClick);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('contextmenu', () => { });
    };
  }, [onTap, onHighlightCreate, onHighlightDelete, decryptedContent, isSelecting, isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current && contentRef.current) {
        const currentScrollTop = containerRef.current.scrollTop;
        const scrollPosition = currentScrollTop;
        const totalHeight = contentRef.current.scrollHeight - containerRef.current.clientHeight;
        const percentRead = totalHeight > 0 ? Math.min(scrollPosition / totalHeight, 1) : 0;
        const scrollDifference = currentScrollTop - lastScrollTop.current;
        let scrollDirection: 'up' | 'down' | 'none' = 'none';

        if (Math.abs(scrollDifference) > 5) {
          scrollDirection = scrollDifference > 0 ? 'down' : 'up';
        }

        console.log(`[CHAPTER_CONTENT] SCROLL: Position: ${scrollPosition}, Percent: ${percentRead.toFixed(4)}, Direction: ${scrollDirection}`);

        if (onScroll) {
          const isAtTop = currentScrollTop <= 10;
          const isAtBottom = Math.abs(totalHeight - currentScrollTop) <= 10;
          const shouldShowUI = isAtTop || isAtBottom;
          onScroll(scrollPosition, percentRead, shouldShowUI, scrollDirection);
        }

        lastScrollTop.current = currentScrollTop;

        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }

        scrollTimeout.current = setTimeout(() => {
          if (onScroll && containerRef.current && contentRef.current) {
            const finalScrollTop = containerRef.current.scrollTop;
            const finalPosition = finalScrollTop;
            const finalTotalHeight = contentRef.current.scrollHeight - containerRef.current.clientHeight;
            const finalPercentRead = finalTotalHeight > 0 ? Math.min(finalPosition / finalTotalHeight, 1) : 0;
            const finalIsAtTop = finalScrollTop <= 10;
            const finalIsAtBottom = Math.abs(finalTotalHeight - finalScrollTop) <= 10;
            onScroll(finalPosition, finalPercentRead, finalIsAtTop || finalIsAtBottom, 'none');
          }
        }, 150);
      }
    };

    const container = containerRef.current;
    if (container) {
      console.log('[CHAPTER_CONTENT] Setting up THE SINGLE scroll listener with direction detection');
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        console.log('[CHAPTER_CONTENT] Removing THE SINGLE scroll listener');
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeout.current) {
          clearTimeout(scrollTimeout.current);
        }
        if (scrollDirectionTimeout.current) {
          clearTimeout(scrollDirectionTimeout.current);
        }
      };
    }
  }, [onScroll]);

  const dynamicPaddingBottom = getContentPaddingBottom(
    footerVisible,
    audioPlayerVisible,
    isMobile
  );

  const chapterStyles = `
    .chapter-content {
      padding: 1.5rem;
      padding-bottom: ${dynamicPaddingBottom};
      min-height: 100vh;
      line-height: 1.75;
      user-select: text;
      -webkit-user-select: text;
      -moz-user-select: text;
      -ms-user-select: text;
      -webkit-touch-callout: default;
    }
    
    .chapter-content p {
      margin-bottom: 1.75em;
      text-indent: 0;
    }
    
    .chapter-content p:last-child {
      margin-bottom: 0;
    }
    
    .chapter-content h1,
    .chapter-content h2,
    .chapter-content h3 {
      margin-top: 2em;
      margin-bottom: 1em;
      line-height: 1.4;
    }
    
    .chapter-content blockquote {
      margin: 2em 0;
      padding-left: 1.5em;
      border-left: 3px solid currentColor;
      opacity: 0.8;
      font-style: italic;
    }
    
    .highlight-mark {
      padding: 2px 0;
      border-radius: 2px;
      cursor: pointer;
      position: relative;
      transition: opacity 0.2s ease, box-shadow 0.2s ease;
    }

    .highlight-mark:hover {
      opacity: 0.8;
      box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
    }

    .highlight-mark:active {
      opacity: 0.6;
    }

    .highlight-yellow {
      background-color: #fbbf24;
      color: #1f2937;
    }

    .highlight-green {
      background-color: #10b981;
      color: white;
    }

    .highlight-blue {
      background-color: #3b82f6;
      color: white;
    }

    .highlight-pink {
      background-color: #ec4899;
      color: white;
    }

    .highlight-orange {
      background-color: #f97316;
      color: white;
    }

    /* Melhorar seleção de texto em mobile */
    @media (max-width: 768px), (pointer: coarse) {
      .chapter-content {
        padding: 1rem;
        padding-bottom: ${dynamicPaddingBottom};
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
        -webkit-touch-callout: default !important;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
      }

      .highlight-mark {
        padding: 4px 2px;
        min-height: 24px;
      }

      .highlight-mark:active {
        background-color: rgba(0, 0, 0, 0.1);
      }
    }

    /* Estilos para indicar que texto pode ser selecionado */
    .chapter-content::selection {
      background: rgba(59, 130, 246, 0.3);
      color: inherit;
    }

    .chapter-content::-moz-selection {
      background: rgba(59, 130, 246, 0.3);
      color: inherit;
    }
  `;

  return (
    <ThemeProvider theme={theme}>
      <style>{chapterStyles}</style>
      <SecurityLayer contentRef={contentRef} allowHighlights={true} />
      <InitialScrollPosition
        containerRef={containerRef}
        initialScrollPosition={initialScrollPosition}
        isInitialPositionReady={isInitialPositionReady && contentLoaded}
        content={content}
      />
      <div
        ref={containerRef}
        className="pulse-scrollable-content"
        style={{
          padding: '1.5rem',
          paddingBottom: getContentPaddingBottom(footerVisible, audioPlayerVisible, isMobile),
          userSelect: 'text',
          WebkitUserSelect: 'text',
          WebkitTouchCallout: 'default'
        }}
        data-testid="chapter-content-container"
      >
        <div
          ref={contentRef}
          className="chapter-content prose prose-sm max-w-none sm:prose lg:prose-lg"
          data-secure-content="true"
          dangerouslySetInnerHTML={{
            __html: (() => {
              if (processHTMLWithHighlights && highlights && highlights.length > 0) {
                console.log('[HIGHLIGHT_DEBUG] Processing HTML with', highlights.length, 'highlights');
                const processedHTML = processHTMLWithHighlights(decryptedContent);
                console.log('[HIGHLIGHT_DEBUG] Processed HTML contains highlights:', processedHTML.includes('highlight-mark'));
                return processedHTML;
              }
              console.log('[HIGHLIGHT_DEBUG] Using original content (no highlights)');
              return decryptedContent;
            })()
          }}
        />
      </div>
    </ThemeProvider>
  );
});

export default ChapterContent;